/**
 * HubServiceCatalog — Hub's read-only view of the Service Catalog: the Domain > Service >
 * Metric hierarchy, styled like Hub's own document-library cards rather than the Catalog's own
 * full-CRUD forms. Reuses the exact on-target/off-target logic and Sparkline/MetricRing widgets
 * from CatalogPage.tsx so "on target" means the same thing in both places. No create/edit/delete
 * anywhere — each service/metric name links out to its live Tracker record for anyone who needs
 * the real thing, the same escape hatch the Initiative Tracker Hub page uses.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { domainsApi, servicesApi, metricsApi, readingsApi } from '../../services/trackerApi';
import { peopleApi } from '../../services/api';
import { Sparkline, MetricRing } from '../CatalogPage';
import { computeMetricStatus, METRIC_STATUS_COLOR } from '../../utils/format';
import type { Domain, Service, Metric, MetricReading } from '../../types/trackerModels';

const DOMAIN_ICON = (name: string) => {
  const n = name.toLowerCase();
  if (n.includes('security')) return 'ri-shield-keyhole-line';
  if (n.includes('it') || n.includes('operations')) return 'ri-server-line';
  return 'ri-folders-line';
};

export const HubServiceCatalog: React.FC = () => {
  const { data: domains = [], isLoading: domainsLoading, error } = useQuery<Domain[]>({ queryKey: ['domains'], queryFn: domainsApi.getAll, staleTime: 0 });
  const { data: services = [], isLoading: servicesLoading } = useQuery<Service[]>({ queryKey: ['services'], queryFn: servicesApi.getAll, staleTime: 0 });
  const { data: metrics = [], isLoading: metricsLoading } = useQuery<Metric[]>({ queryKey: ['metrics'], queryFn: metricsApi.getAll, staleTime: 0 });
  const { data: people = [] } = useQuery<{ personId: string; personName: string }[]>({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const personName = (id: string) => people.find((p) => p.personId === id)?.personName || 'Unowned';

  const readingQueries = useQueries({
    queries: metrics.map((m) => ({
      queryKey: ['readings', m.metricId],
      queryFn: () => readingsApi.list(m.metricId),
      enabled: !!m.metricId,
    })),
  });
  const readingsByMetric: Record<string, MetricReading[]> = {};
  metrics.forEach((m, i) => {
    readingsByMetric[m.metricId] = ((readingQueries[i]?.data as MetricReading[]) || []).slice().sort((a, b) => a.date.localeCompare(b.date));
  });

  const onTargetCount = metrics.filter((m) => computeMetricStatus(m, readingsByMetric[m.metricId] || []) === 'on-target').length;
  const offTargetCount = metrics.filter((m) => computeMetricStatus(m, readingsByMetric[m.metricId] || []) === 'off-target').length;

  // domains/services/metrics are three independent queries with no ordering guarantee - gating
  // on domainsLoading alone let the page flip to "No services in this domain yet" for every
  // domain whenever services/metrics simply hadn't resolved yet.
  const isLoading = domainsLoading || servicesLoading || metricsLoading;

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-archive-drawer-line"
        title="Service Catalog"
        subtitle="Domains, services and the metrics they're measured by — read-only"
        breadcrumbs={[{ label: 'Hub' }, { label: 'Service Catalog' }]}
      />

      {isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
        </div>
      ) : error ? (
        <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Failed to load the Service Catalog.</div>
      ) : domains.length === 0 ? (
        <div className="np-card">
          <div className="np-card-body py-5 text-center">
            <i className="ri-archive-drawer-line" style={{ fontSize: '3rem', color: 'var(--bs-secondary-color)' }}></i>
            <h6 className="mt-3 np-sub">Nothing in the catalog yet.</h6>
          </div>
        </div>
      ) : (
        <>
          <div className="np-grid np-grid-4">
            <div className="np-card">
              <div className="np-card-body np-stat">
                <span className="np-stat-icon np-tone-primary"><i className="ri-stack-line"></i></span>
                <p className="np-stat-label">Domains</p>
                <p className="np-stat-value np-tabular">{domains.length}</p>
              </div>
            </div>
            <div className="np-card">
              <div className="np-card-body np-stat">
                <span className="np-stat-icon np-tone-info"><i className="ri-server-line"></i></span>
                <p className="np-stat-label">Services</p>
                <p className="np-stat-value np-tabular">{services.length}</p>
              </div>
            </div>
            <div className="np-card">
              <div className="np-card-body np-stat">
                <span className="np-stat-icon np-tone-success"><i className="ri-checkbox-circle-line"></i></span>
                <p className="np-stat-label">Metrics on target</p>
                <p className="np-stat-value np-tabular">{onTargetCount} <span className="np-sub" style={{ fontSize: '0.9rem', fontWeight: 500 }}>/ {metrics.length}</span></p>
              </div>
            </div>
            <div className={`np-card ${offTargetCount > 0 ? 'np-card-accent-danger' : ''}`}>
              <div className="np-card-body np-stat">
                <span className="np-stat-icon np-tone-danger"><i className="ri-alert-line"></i></span>
                <p className="np-stat-label">Needs attention</p>
                <p className="np-stat-value np-tabular">{offTargetCount}</p>
              </div>
            </div>
          </div>

          {domains.map((d) => {
            const domainServices = services.filter((s) => s.domainId === d.domainId);
            const domainMetrics = metrics.filter((m) => domainServices.some((s) => s.serviceId === m.serviceId));
            return (
              <div className="np-card" key={d.domainId}>
                <div className="np-card-head">
                  <span className="np-icon-badge np-tone-primary"><i className={DOMAIN_ICON(d.name)}></i></span>
                  <h2>{d.name}</h2>
                  <span className="np-sub">{domainServices.length} service{domainServices.length === 1 ? '' : 's'} · {domainMetrics.length} metric{domainMetrics.length === 1 ? '' : 's'}</span>
                </div>
                <div className="np-card-body">
                  {domainServices.length === 0 ? (
                    <p className="np-sub mb-0 fst-italic"><i className="ri-service-line me-1"></i>No services in this domain yet.</p>
                  ) : (
                    domainServices.map((s) => {
                      const serviceMetrics = metrics.filter((m) => m.serviceId === s.serviceId);
                      return (
                        <div className="np-row-card" key={s.serviceId}>
                          <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
                            <Link to={`/tracker/catalog/services/${s.serviceId}`} className="hub-overview-title text-truncate">{s.name}</Link>
                            <span className="np-sub flex-shrink-0" style={{ fontSize: '0.72rem' }}>{personName(s.ownerId)}</span>
                          </div>
                          {serviceMetrics.length === 0 ? (
                            <p className="np-sub mb-0 fst-italic" style={{ fontSize: '0.8rem' }}><i className="ri-line-chart-line me-1"></i>No metrics tracked yet.</p>
                          ) : (
                            serviceMetrics.map((m) => {
                              const readings = readingsByMetric[m.metricId] || [];
                              const status = computeMetricStatus(m, readings);
                              const latest = readings[readings.length - 1]?.value;
                              const color = `var(--bs-${METRIC_STATUS_COLOR[status]})`;
                              return (
                                <Link
                                  key={m.metricId}
                                  to={`/tracker/catalog/metrics/${m.metricId}`}
                                  className="np-meta-row"
                                  style={{ textDecoration: 'none' }}
                                >
                                  <span className="np-meta-label text-truncate">
                                    <span className="hub-status-dot" style={{ background: color }}></span>
                                    {m.name}
                                    <span className="np-sub" style={{ marginLeft: '0.4rem', fontSize: '0.72rem' }}>
                                      {latest != null ? `${latest} ${m.unit}` : 'No readings'}{m.target != null ? ` · target ${m.target}${m.unit}` : ''}
                                    </span>
                                  </span>
                                  {readings.length > 0 && readings.length < 3 ? (
                                    <MetricRing value={readings[readings.length - 1].value} target={m.target} direction={m.direction} color={color} />
                                  ) : readings.length > 0 ? (
                                    <Sparkline values={readings.map((r) => r.value)} color={color} />
                                  ) : (
                                    <span className="np-sub" style={{ fontSize: '0.72rem' }}>{m.cadence}</span>
                                  )}
                                </Link>
                              );
                            })
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default HubServiceCatalog;
